import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order, OrderType, StatusButtonsConfig } from 'models/order';
import CheckIcon from '@mui/icons-material/Check';
import { getOrderDetailsStatusButtons } from 'components/order-details/utils/get-order-details-sub-nav-buttons';
import ReceivedByLabDialog from 'components/orders/order-details/received-by-lab-dialog.component';
import SendToClinicDialog from 'components/orders/order-details/send-to-clinic-dialog.component';
import ApprovedOrderDialog from 'components/orders/order-details/approve-order-dialog.component';
import useBus from 'use-bus';
import { isCustomer, isSupplier } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type TopBarOrderStatusSectionProps = {
    order: Order;
    onHandleDeliveryNoteCreation: () => void;
};
const TopBarOrderStatusSection: FC<TopBarOrderStatusSectionProps> = ({
    order,
    onHandleDeliveryNoteCreation,
}: TopBarOrderStatusSectionProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    // DD-3391: among external users only the elevated tier may send an order to the
    // clinic - the backend refuses the rest - so the others are not offered a button
    // that can only fail. This is the one place an external user is offered it; the
    // order list's context menu is internal-technician only.
    const canSendToClinic =
        !permissions.isExternalTechnician(currentUser.role) || permissions.isElevatedExternalUser(currentUser.role);
    const [statusConfig, setStatusConfig] = useState<StatusButtonsConfig>();
    const [openReceivedByLabDialog, setReceivedByLabDialog] = useState(false);
    const [openSentByLabDialog, setOpenSentByLabDialog] = useState(false);
    const [qrCodeTechnicianId, setQrCodeTechnicianId] = useState<string>();
    const [openApproveOrderDialog, setApproveOrderDialog] = useState(false);

    useEffect(() => {
        setStatusConfig(getOrderDetailsStatusButtons(order.status));
    }, [order]);

    //Todo - Need to check QR things
    useBus(
        'qr-send-to-clinic-happy',
        ({ payload }) => {
            setQrCodeTechnicianId(payload.technicianId);
        },
        [],
    );

    const handleCloseDialog = () => {
        setReceivedByLabDialog(false);
        setOpenSentByLabDialog(false);
        setQrCodeTechnicianId(undefined);
        setApproveOrderDialog(false);
    };

    return (
        <Fragment>
            {isSupplier(currentOrganization._id, order.supplier._id) && statusConfig?.acceptByLab ? (
                <Button variant="contained" onClick={() => setReceivedByLabDialog(true)} startIcon={<CheckIcon />}>
                    {translate('orders:ACCEPTED_BY_LAB_BUTTON')}
                </Button>
            ) : undefined}
            {isSupplier(currentOrganization._id, order.supplier._id) && statusConfig?.sentByLab && canSendToClinic ? (
                <Button variant="contained" onClick={() => setOpenSentByLabDialog(true)} startIcon={<CheckIcon />}>
                    {translate('orders:SENT_BY_LAB_BUTTON')}
                </Button>
            ) : undefined}
            {isCustomer(currentOrganization._id, order.customer._id) &&
            order.type === OrderType.ACTION_BASED &&
            statusConfig?.approveOrder &&
            order?.isProductionFinished ? (
                <Button
                    onClick={() => setApproveOrderDialog(true)}
                    variant="contained"
                    startIcon={<CheckIcon />}
                    data-test="order-details.top-bar.order-status-approved-by-dentist-button"
                >
                    {translate('orders:APPROVED_BY_DENTIST_BUTTON')}
                </Button>
            ) : undefined}

            {openReceivedByLabDialog ? (
                <ReceivedByLabDialog open={openReceivedByLabDialog} onClose={handleCloseDialog} orderId={order._id} />
            ) : undefined}

            {openSentByLabDialog ? (
                <SendToClinicDialog
                    open={openSentByLabDialog}
                    onClose={handleCloseDialog}
                    orderId={order._id}
                    qrCodeTechnicianId={qrCodeTechnicianId}
                    onHandleDeliveryNoteCreation={onHandleDeliveryNoteCreation}
                />
            ) : undefined}

            {openApproveOrderDialog ? (
                <ApprovedOrderDialog open={openApproveOrderDialog} onClose={handleCloseDialog} orderId={order._id} />
            ) : undefined}
        </Fragment>
    );
};
export default TopBarOrderStatusSection;
